import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import InputImage from "../project/component/Input/InputImage";
import { FaExclamationCircle, FaCheckCircle } from "react-icons/fa";
import {
    ClipboardPenLine,
    Contact2,
    Loader,
    School,
    University,
    AppWindow,
    UsersIcon,
    Package2,
    GraduationCap,
    Building,
    Dna,
    TabletSmartphone,
    Map,
    School2,
    School2Icon,
    Building2
} from "lucide-react";
import { getToken } from "../project/helpers/Jwt";
import { MdCastForEducation } from "react-icons/md";
import SectionForm from "./component/requestapp/SectionForm";
import InputGroupForm from "./component/requestapp/InputGroupForm";
import ErrorFieldForm from "./component/requestapp/ErrorFieldForm";
import ErrorFocusForm from "./component/requestapp/ErrorFocusForm";
import Signature from "./component/requestapp/SignatureForm";
import PlatformBadge from "./component/requestapp/PlatformBadge";

const FormRequestApp = () => {
    const { type } = useParams();
    const navigate = useNavigate();

    const LoadingField = ({ text = "Loading..." }) => (
        <div className="flex items-center gap-3 p-3 mt-3 rounded-lg border bg-blue-50 border-blue-200">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

            <div>
                <p className="text-sm font-medium text-blue-700">
                    {text}
                </p>

                <p className="text-xs text-blue-500">
                    Mohon tunggu sebentar...
                </p>
            </div>
        </div>
    );

    const [initialValues, setInitialValues] = useState({
        type,
        software: "",
        app_mode: "",
        education_level: "",
        level_region: "",
        total_staff: "",
        total_teacher: "",
        total_student: "",
        pic_name: "",
        pic_position: "",
        pic_contact: "",
        pic_password: "password",
        leader: null,
        corp_type: "",
        corp_name: "",
        institute_name: "",
        province: "",
        city: "",
        district: "",
        office_address: "",
        contact_official: "",
        email_official: "",
        web_official: "",
        platform_version: "hijrah.co.id",
        logo: null,
        stamp: null,
        signature: null,
    });

    const softwareSystems = [
        {
            value: "zone", title: "Citizen Super App (one-stop solution)",
            description: "Kelola Data Warga, Surat Menyurat, Layanan Terpadu, Laporan dan Arsip"
        },
        {
            value: "academic", title: "Student Information System",
            description: "Kelola Data Siswa, Guru, Pegawai, Jadwal, Nilai, Payroll, Absensi dan Arsip"
        },
        {
            value: "umroh.com", title: "Multi-Branch Control (Travel Agent)",
            description: "Kelola Data Customer, Paket Perjalanan, Kuota, Booking, Payment, Supplier, Sistem Agensi dan Monitoring Progress"
        },
        {
            value: "ehajj", title: "B2G Seat Booking (KBIH)",
            description: "Kelola Kuota, Distribusi, Porsi, Paket, Booking, Penjualan Masyair dan Penempatan"
            // Kelola Kuota, Biaya dan Penjualan Masyair
        },
        {
            value: "pos", title: "Point of Sales",
            description: "Kelola Data Penjualan, Customer, Produk, Stok, Transaksi, Supplier, Distribusi dan Laporan"
        },
        {
            value: "core_cooperative_system", title: "Core Cooperative System",
            description: "Kelola Data Pegawai, Anggota, Nasabah, Sistem Tabungan, Penagihan, Keuangan, Aset, Transaksi dan Arsip"
        },
    ]

    const educationLevel = [
        {
            value: "pend-dasar",
            title: "Pendidikan Dasar",
            description: "SD / SMP",
            platformName: "SIS"
        },
        {
            value: "pend-menengah",
            title: "Pendidikan Menengah",
            description: "SMA / SMK",
            platformName: "SIS"
        },
        {
            value: "pend-tinggi",
            title: "Pendidikan Tinggi",
            description: "Universitas / Institut",
            platformName: "AIS"
        },
    ];

    const regionLevel = [
        {
            value: "province",
            title: "Tingkat Provinsi"
        },
        {
            value: "city",
            title: "Tingkat Kabupaten/ Kota"
        },
        {
            value: "district",
            title: "Tingkat Kecamatan"
        },
    ];

    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const messageRef = useRef(null); // 🔹 Tambahkan ref
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [resetImage, setResetImage] = useState(0);

    useEffect(() => {
        if (successMessage || errorMessage) {
            messageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [successMessage, errorMessage]);

    const endpointProvinces = "/api/zone/region/provinces";
    const [dataProvinces, setDataProvinces] = useState([]);
    const [selectedProvince, setProvince] = useState("");
    const [loadingProvinces, setLoadingProvinces] = useState(true);

    const endpointCities = "/api/zone/region/cities";
    const [dataCities, setDataCities] = useState([]);
    const [selectedCity, setCity] = useState("");
    const [loadingCities, setLoadingCities] = useState(false);

    const endpointDistrict = "/api/zone/region/districts";
    const [dataDistricts, setDataDistricts] = useState([]);
    const [selectedDistrict, setDistrict] = useState("");
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    const [platformName, setPlatformName] = useState("");

    const fetchData = async (endpoint, { setData, setLoading }) => {
        try {
            setLoading(true);
            const startTime = Date.now();
            const response = await axios.get(endpoint, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                }
            });
            setData(response.data || []);

            // Minimal tampil loading 800ms
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 800 - elapsed);

            await new Promise(resolve =>
                setTimeout(resolve, remaining)
            );
        } catch (err) {
            console.error('Error fetching price data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(`${BASE_URL}${endpointProvinces}`, {
            setData: setDataProvinces,
            setLoading: setLoadingProvinces
        });
    }, []);

    useEffect(() => {
        if (!selectedProvince) return;

        setLoadingCities(true);

        fetchData(
            `${BASE_URL}${endpointCities}?province=${encodeURIComponent(selectedProvince)}`,
            {
                setData: setDataCities,
                setLoading: setLoadingCities,
            }
        );
    }, [selectedProvince]);

    useEffect(() => {
        if (!selectedCity) return;

        setLoadingDistricts(true);

        fetchData(
            `${BASE_URL}${endpointDistrict}?city=${encodeURIComponent(selectedCity)}`,
            {
                setData: setDataDistricts,
                setLoading: setLoadingDistricts,
            }
        );
    }, [selectedCity]);

    const onSubmit = async (values, { setSubmitting, resetForm }) => {
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const response = await axios.post(
                `${BASE_URL}/api/superapps/project/requestapp`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            if (response.status === 200 || response.status === 201) {
                setSuccessMessage("Form berhasil dikirim!");
                // resetForm();
                // setResetImage(prev => prev + 1);
            } else {
                setErrorMessage(
                    response.data?.message || "Gagal mengirim form, coba lagi."
                );
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setErrorMessage(
                error.response?.data?.message || "Terjadi kesalahan saat mengirim form."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const validationSchema = Yup.object({
        software: Yup.string().required("Pilih Software System"),
        app_mode: Yup.string().required("Pilih mode aplikasi"),
        education_level: Yup.string()
            .when("software", {
                is: (value) => ["academic"].includes(value),
                then: (schema) =>
                    schema.required("Pilih Level Pendidikan"),
                otherwise: (schema) => schema.notRequired(),
            }),
        level_region: Yup.string().when(
            ["software", "app_mode"],
            {
                is: (software, appMode) =>
                    software === "zone" || (software === "academic" && appMode == "Ready-made Apps"),
                then: (schema) =>
                    schema.required("Pilih Level Wilayah"),
                otherwise: (schema) => schema.notRequired(),
            }
        ),
        province: Yup.string()
            .when("level_region", {
                is: (value) =>
                    ["province", "city", "district"].includes(value),
                then: (schema) =>
                    schema.required("Provinsi wajib dipilih"),
                otherwise: (schema) =>
                    schema.notRequired(),
            }),
        city: Yup.string()
            .when("level_region", {
                is: (value) => ["city", "district"].includes(value),
                then: (schema) => schema.required("Kabupaten/ Kota wajib dipilih"),
                otherwise: (schema) => schema.notRequired()
            }),
        district: Yup.string()
            .when("level_region", {
                is: (value) => ["district"].includes(value),
                then: (schema) => schema.required("Kecamatan wajib dipilih"),
                otherwise: (schema) => schema.notRequired()
            }),
        total_staff: Yup.number()
            .when("software", {
                is: (value) => value != "zone",
                then: (schema) =>
                    schema.required("Isi total staf").positive("Harus bernilai positif"),
                otherwise: (schema) => schema.notRequired(),
            }),
        total_teacher: Yup.number().when("software", {
            is: (value) => ["academic"].includes(value),
            then: (schema) =>
                schema.required("Isi total guru/dosen").positive("Harus bernilai positif"),
            otherwise: (schema) => schema.notRequired(),
        }),
        total_student: Yup.number().when("software", {
            is: (value) => ["academic"].includes(value),
            then: (schema) =>
                schema.required("Isi total siswa/mahasiswa").positive("Harus bernilai positif"),
            otherwise: (schema) => schema.notRequired(),
        }),
        logo: Yup.mixed().required("Logo wajib diisi"),
        stamp: Yup.mixed().required("Cap/stempel wajib diisi"),

        corp_type: Yup.string().when("software", {
            is: (value) => value != "zone",
            then: (schema) =>
                schema.required("Badan hukum wajib dipilih"),
            otherwise: (schema) => schema.notRequired(),
        }),
        corp_name: Yup.string().when("software", ([software], schema) => {
            return schema.required(
                software === "zone"
                    ? "Nama Instansi wajib diisi"
                    : "Nama Institusi wajib diisi"
            );
        }),
        institute_name: Yup.string().when("software", {
            is: (value) => value != "zone",
            then: (schema) =>
                schema.required("Nama Brand/ Hak Paten wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),
        leader: Yup.string().required("Nama Pimpinan wajib diisi"),
        office_address: Yup.string().required("Alamat Kantor wajib diisi"),
        contact_official: Yup.string().required("Kontak Resmi wajib diisi"),
        email_official: Yup.string().required("Email Resmi wajib diisi"),
        pic_name: Yup.string().required("Nama PIC wajib diisi"),
        pic_contact: Yup.string().required("Contact PIC wajib diisi"),
        pic_position: Yup.string().required("Jabatan PIC wajib diisi"),
        // web_official: Yup.string().url("Format URL tidak valid"),
        signature: Yup.mixed().required("Tanda tangan wajib diisi"),
    });

    return (
        <div className="pt-20 pb-20 px-3 sm:px-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl p-6 sm:p-8 border border-gray-100">
                <h2 className="flex items-center justify-center gap-3 text-2xl sm:text-3xl font-bold text-[#f29f02]">
                    <ClipboardPenLine className="w-8 h-8 sm:w-10 sm:h-10" />
                    <span>Form Request Apps</span>
                </h2>

                <div ref={messageRef}>
                    {successMessage && (
                        <div className="mb-4 bg-green-50 border border-green-400 text-green-700 p-3 rounded-md text-sm flex items-center gap-2">
                            <FaCheckCircle />
                            <span>{successMessage}</span>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="mb-4 bg-red-50 border border-red-300 text-red-700 p-3 rounded-md text-sm flex items-start gap-2">
                            <FaExclamationCircle className="mt-0.5" />
                            <span>{errorMessage}</span>
                        </div>
                    )}
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {({ setFieldValue, values, isSubmitting }) => {
                        const selectedSoftware = softwareSystems.find(
                            item => item.value === values.software
                        );

                        var isAcademicApp = values.software == "academic";
                        var isZoneApp = values.software == "zone";
                        var isReadyMadeApp = values.app_mode === "Ready-made Apps";

                        var isAcademicReadyMadeApp = isAcademicApp && isReadyMadeApp;
                        var isAcademicCustomApp = isAcademicApp && !isReadyMadeApp;

                        var isZoneReadyMadeApp = isZoneApp && isReadyMadeApp;
                        var isZoneCustomApp = isZoneApp && !isReadyMadeApp;

                        var isNeedRegionData = isZoneApp || isAcademicReadyMadeApp;
                        var isNeedStaff = !isZoneApp;

                        const institutionField = [
                            {
                                type: "select", label: "Badan Hukum/ Usaha", name: "corp_type",
                                hidden: (values) => isZoneApp,
                                options: [
                                    { value: "", label: "Pilih Badan Hukum/ Usaha" },
                                    { value: "Yayasan", label: "Yayasan" },
                                    { value: "Perkumpulan", label: "Perkumpulan" },
                                    { value: "Badan Wakaf", label: "Badan Wakaf" },
                                    { value: "Badan Hukum Publik", label: "Badan Hukum Publik" },
                                    { value: "PTN-BH", label: "PTN-BH" },
                                    { disabled: true, value: null, label: "──────────" },
                                    { value: "Koperasi/ BMT", label: "Koperasi/ BMT" },
                                    { value: "Perseroan Terbatas (PT)", label: "Perseroan Terbatas (PT)" },
                                    { value: "BUMN/BUMD", label: "BUMN/BUMD" },
                                    { disabled: true, value: null, label: "──────────" },
                                    { value: "Unit Dagang (UD)", label: "Unit Dagang (UD)" },
                                    { value: "Commanditaire Vennootschap (CV)", label: "Commanditaire Vennootschap (CV)" },
                                    { value: "Firma (Fa)", label: "Firma (Fa)" },
                                ]
                            },
                            {
                                type: "text", label: "Nama Institusi Terdaftar", name: "corp_name",
                                hidden: (values) => isZoneApp,
                            },
                            {
                                type: "text", label: "Nama Brand/ Hak Paten", name: "institute_name",
                                hidden: (values) => isZoneApp,
                            },
                            {
                                type: "text", label: "Nama Instansi Pemerintahan", name: "corp_name",
                                disabled: true, hint: "Autofill Instansi Pemerintahan",
                                hidden: (values) => !isZoneApp,
                            },
                            { type: "text", label: "Nama Pimpinan", name: "leader" },
                            { type: "text", label: "Alamat Kantor", name: "office_address", as: "textarea" },
                            { type: "text", label: "Telp. Kantor", name: "contact_official" }, //phone
                            { type: "email", label: "Email Resmi", name: "email_official", hint: "user@email.com" },
                            { type: "url", label: "Website Resmi", name: "web_official", hint: "https://domain.com" },
                        ];

                        return <Form className="space-y-5">
                            <ErrorFocusForm />
                            <SectionForm
                                key="section_software"
                                icon={<AppWindow className="w-5 h-5" />} title="Software System" color="blue">
                                <Field
                                    key="software"
                                    name="software">
                                    {({ field, form }) => (
                                        <select
                                            {...field}
                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300"
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setDataProvinces([]);
                                                setDataCities([]);
                                                setDataDistricts([]);
                                                form.setFieldValue("level_region", "")
                                                form.setFieldValue("province", "");
                                                form.setFieldValue("city", "");
                                                form.setFieldValue("district", "");

                                                fetchData(`${BASE_URL}${endpointProvinces}`, {
                                                    setData: setDataProvinces,
                                                    setLoading: setLoadingProvinces
                                                });

                                                const softwareValue = e.target.value;
                                                setPlatformName("");

                                                if (softwareValue == "zone") {
                                                    setPlatformName("Zone");
                                                } else if (softwareValue == "umroh.com") {
                                                    setPlatformName("Umroh.com");
                                                } else if (softwareValue == "ehajj") {
                                                    setPlatformName("E-Hajj");
                                                } else if (softwareValue == "pos") {
                                                    setPlatformName("L`Bayt");
                                                } else if (softwareValue == "core_cooperative_system") {
                                                    setPlatformName("L`Bayt");
                                                }
                                            }}>
                                            <option value="">-- Pilih Software System --</option>

                                            {softwareSystems.map(({ value, title, description }) => (
                                                <option key={value} value={value}>
                                                    {title}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </Field>
                                <ErrorFieldForm name="software" />

                                {selectedSoftware && (
                                    <div className="mt-3 rounded-md bg-blue-50 p-3">
                                        <h4 className="font-medium text-blue-700">
                                            {selectedSoftware.title}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {selectedSoftware.description}
                                        </p>
                                    </div>
                                )}
                            </SectionForm>

                            <SectionForm
                                key="section_app_mode"
                                data-field="app_mode"
                                icon={<TabletSmartphone className="w-5 h-5" />}
                                title="Mode Aplikasi"
                                color="blue">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {[
                                        {
                                            value: "Ready-made Apps",
                                            title: "Ready-made Apps",
                                            subtitle: "Siap Pakai",
                                            icon: "🚀",
                                            features: [
                                                "Implementasi Cepat",
                                                "Fitur Standar",
                                            ],
                                            color:
                                                "border-emerald-300 bg-emerald-50",
                                        },
                                        {
                                            value: "Custom Apps",
                                            title: "Custom Apps",
                                            subtitle: "Sesuai Kebutuhan",
                                            icon: "🛠️",
                                            features: [
                                                "Custom Branding",
                                                "Custom Workflow",
                                                "Integrasi Sistem",
                                            ],
                                            color:
                                                "border-amber-300 bg-amber-50",
                                        },
                                    ].map((item) => {
                                        const isReadyMadeApp = item.value === "Ready-made Apps";
                                        const selected =
                                            values.app_mode === item.value;

                                        return (
                                            <div
                                                key={item.value}
                                                onClick={() =>
                                                    setFieldValue(
                                                        "app_mode",
                                                        item.value
                                                    )
                                                }
                                                className={`
                    cursor-pointer rounded-xl border p-4 transition-all
                    ${selected
                                                        ? `${item.color} ring-2 ring-offset-1 ${isReadyMadeApp
                                                            ? "ring-emerald-300"
                                                            : "ring-amber-300"
                                                        }`
                                                        : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                                                    }
                `}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="text-2xl">
                                                            {item.icon} {(platformName && isReadyMadeApp) && (
                                                                <PlatformBadge name={platformName} />
                                                            )}
                                                        </div>

                                                        <h3 className="font-semibold mt-2">
                                                            {item.title}
                                                        </h3>

                                                        <p className="text-xs text-gray-500">
                                                            {item.subtitle}
                                                        </p>
                                                    </div>

                                                    {selected && (
                                                        <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center">
                                                            ✓

                                                        </div>

                                                    )}
                                                </div>

                                                <ul className="mt-3 space-y-1 text-xs text-gray-600">
                                                    {item.features.map((feature) => (
                                                        <li key={feature}>
                                                            ✓ {feature}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>

                                <ErrorFieldForm name="app_mode" />
                            </SectionForm>

                            {isAcademicApp && (
                                <SectionForm
                                    key="section_education_level"
                                    data-field="education_level"
                                    icon={<School2Icon className="w-5 h-5" />}
                                    title="Level Pendidikan"
                                    color="blue">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {educationLevel.map((item) => {
                                            const selected =
                                                values.education_level === item.value;
                                            return (
                                                <div
                                                    key={item.value}
                                                    onClick={() =>
                                                        setFieldValue(
                                                            "education_level",
                                                            item.value
                                                        )
                                                    }
                                                    className={`
                            cursor-pointer rounded-lg border p-4 transition-all
                            ${selected
                                                            ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                                                            : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                                                        }
                        `}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <h3 className="font-semibold text-sm">
                                                                {item.title} <PlatformBadge name={item.platformName} />
                                                            </h3>


                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {item.description}
                                                            </p>
                                                        </div>

                                                        {selected && (
                                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs">
                                                                ✓
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <ErrorFieldForm name="education_level" />
                                </SectionForm>
                            )}

                            {isZoneApp &&
                                (
                                    <SectionForm
                                        data-field="level_region"
                                        icon={<Map className="w-5 h-5" />}
                                        title="Level Instansi Pemerintahan"
                                        color="blue"
                                        key="section_level_region">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            {regionLevel.map((item) => {
                                                const selected = values.level_region === item.value;
                                                return (
                                                    <div
                                                        key={item.value}
                                                        onClick={() =>
                                                            setFieldValue(
                                                                "level_region",
                                                                item.value
                                                            )
                                                        }
                                                        className={`
                        cursor-pointer rounded-lg border p-4 transition-all
                        ${selected ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                                                            }
                    `}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <h3 className="font-semibold text-sm">
                                                                    {item.title}
                                                                </h3>
                                                            </div>

                                                            {selected && (
                                                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs">
                                                                    ✓
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <ErrorFieldForm name="level_region" />
                                    </SectionForm>
                                )}

                            {isAcademicReadyMadeApp && (
                                <SectionForm
                                    icon={<Map className="w-5 h-5" />}
                                    title="Level Wilayah"
                                    color="blue"
                                    key="section_level_region"> <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {[
                                            {
                                                value: "province",
                                                title: "Provinsi"
                                            },
                                            {
                                                value: "city",
                                                title: "Kabupaten/ Kota"
                                            }
                                        ].map((item) => {
                                            const selected = values.level_region === item.value;
                                            return (
                                                <div
                                                    key={item.value}
                                                    onClick={() =>
                                                        setFieldValue(
                                                            "level_region",
                                                            item.value
                                                        )
                                                    }
                                                    className={`
                        cursor-pointer rounded-lg border p-4 transition-all
                        ${selected ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                                                        }
                    `}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <h3 className="font-semibold text-sm">
                                                                {item.title}
                                                            </h3>
                                                        </div>

                                                        {selected && (
                                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs">
                                                                ✓
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <ErrorFieldForm name="level_region" />
                                </SectionForm>
                            )}

                            {isNeedRegionData && (
                                <SectionForm
                                    icon={<AppWindow className="w-5 h-5" />}
                                    title="Wilayah"
                                    color="blue"
                                    key="section_region">
                                    {["province", "city", "district"].includes(values.level_region) && (
                                        <>
                                            {loadingProvinces ? (
                                                <LoadingField text="Memuat provinsi..." />
                                            ) : (
                                                <InputGroupForm key={"province"} label={"Province"}>
                                                    <Field name="province">
                                                        {({ field, form }) => (
                                                            <select
                                                                {...field}
                                                                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300"
                                                                onChange={(e) => {
                                                                    field.onChange(e);

                                                                    const value = e.target.value;

                                                                    setProvince(value);
                                                                    setDataCities([]);
                                                                    setDataDistricts([]);
                                                                    form.setFieldValue("city", "");


                                                                    if (values.level_region == "province") {
                                                                        form.setFieldValue("corp_name", `PEMERINTAH PROVINSI ${value}`)
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">Pilih Provinsi</option>

                                                                {dataProvinces.map((province) => (
                                                                    <option
                                                                        key={province.id}
                                                                        value={province.name}
                                                                    >
                                                                        {province.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </Field>
                                                    <ErrorFieldForm name="province" />
                                                </InputGroupForm>
                                            )}
                                        </>
                                    )}

                                    {["city", "district"].includes(values.level_region) && (
                                        <>
                                            {loadingCities ? (
                                                <LoadingField text="Memuat kota/kabupaten..." />
                                            ) : (
                                                <InputGroupForm key={"city"} label={"Kota/ Kabupaten"}>
                                                    <Field name="city">
                                                        {({ field, form }) => (
                                                            <select
                                                                {...field}
                                                                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300"
                                                                onChange={(e) => {
                                                                    field.onChange(e);

                                                                    const value = e.target.value;

                                                                    setCity(value);
                                                                    setDataDistricts([]);
                                                                    form.setFieldValue("district", "");

                                                                    if (values.level_region == "city") {
                                                                        form.setFieldValue("corp_name", `PEMERINTAH ${value}`)
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">Pilih Kota/Kabupaten</option>

                                                                {dataCities.map((city) => (
                                                                    <option
                                                                        key={city.id}
                                                                        value={city.name}
                                                                    >
                                                                        {city.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </Field>
                                                    <ErrorFieldForm name="city" />
                                                </InputGroupForm>
                                            )}
                                        </>
                                    )}

                                    {["district"].includes(values.level_region) && (
                                        <>
                                            {loadingDistricts ? (
                                                <LoadingField text="Memuat kecamatan..." />
                                            ) : (
                                                <InputGroupForm key={"district"} label={"Kecamatan"}>
                                                    <Field name="district">
                                                        {({ field, form }) => (
                                                            <select
                                                                {...field}
                                                                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300"
                                                                onChange={(e) => {
                                                                    field.onChange(e);

                                                                    const value = e.target.value;

                                                                    setDistrict(value);
                                                                    if (values.level_region == "district") {
                                                                        form.setFieldValue("corp_name", `KECAMATAN ${value}`)
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">Pilih Kecamatan</option>

                                                                {dataDistricts.map((district) => (
                                                                    <option
                                                                        key={district.id}
                                                                        value={district.name}
                                                                    >
                                                                        {district.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </Field>

                                                    <ErrorFieldForm name="district" />
                                                </InputGroupForm>
                                            )}
                                        </>
                                    )}
                                </SectionForm>)}

                            {isAcademicApp && (
                                <SectionForm
                                    icon={<GraduationCap className="w-5 h-5" />}
                                    title="Pengaturan Akademik"
                                    color="blue"
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {[
                                            { label: "Guru/Dosen", name: "total_teacher" },
                                            { label: "Siswa/Mahasiswa", name: "total_student" },
                                        ].map(({ label, name }) => (
                                            <InputGroupForm key={name} label={label}>
                                                <Field
                                                    type="number"
                                                    name={name}
                                                    className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-gray-300"
                                                />
                                                <ErrorFieldForm name={name} />
                                            </InputGroupForm>
                                        ))}
                                    </div>
                                </SectionForm>
                            )}

                            {isNeedStaff && (
                                <SectionForm
                                    icon={<Building className="w-5 h-5" />}
                                    title="Pengaturan Kepegawaian"
                                    color="blue"
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-1 gap-3">
                                        {[
                                            { label: "Staf/Pegawai", name: "total_staff" },
                                        ].map(({ label, name }) => (
                                            <InputGroupForm key={name} label={label}>
                                                <Field
                                                    type="number"
                                                    name={name}
                                                    className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-gray-300"
                                                />
                                                <ErrorFieldForm name={name} />
                                            </InputGroupForm>
                                        ))}
                                    </div>
                                </SectionForm>
                            )}

                            <SectionForm
                                icon={<Building2 className="w-5 h-5" />}
                                title="Data Institusi"
                                color="blue">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div
                                        className="flex flex-col items-center border-2 border-dashed border-yellow-300 rounded-lg p-3 bg-white hover:bg-yellow-50 transition">
                                        <InputImage
                                            data-field="logo"
                                            resetTrigger={resetImage}
                                            attr={{
                                                name: "logo",
                                                placeholder: "Logo Institusi",
                                                hint: "PNG (Tanpa Background)",
                                                height: 180,
                                                width: 180,
                                            }}
                                            handleCroppedFoto={(v) => {
                                                setFieldValue("logo", v);
                                            }}
                                        />
                                        <InputImage
                                            data-field="stamp"
                                            resetTrigger={resetImage}
                                            attr={{
                                                name: "stamp",
                                                placeholder: "Stempel Institusi",
                                                hint: "PNG (Tanpa Background)",
                                                height: 180,
                                                width: 180,
                                            }}
                                            handleCroppedFoto={(v) => {
                                                setFieldValue("stamp", v);
                                            }}
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        {institutionField
                                            .filter(field => !field.hidden?.(values))
                                            .map(({ label, name, hint, as, type, options, disabled }) => (
                                                type == "select" ?
                                                    <InputGroupForm key={name} label={label} data-field={name}>
                                                        <Field
                                                            as="select"
                                                            name={name}
                                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]">

                                                            {options.map((opt) => (
                                                                <option disabled={opt.disabled ?? false} key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </Field>
                                                        <ErrorFieldForm name={name} />
                                                    </InputGroupForm> :
                                                    <InputGroupForm key={name} label={label} data-field={name}>
                                                        <Field
                                                            type={type || "text"}
                                                            name={name}
                                                            disabled={disabled ?? false}
                                                            placeholder={hint?.trim() || `Input ${label}`}
                                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-yellow-300"
                                                        />
                                                        <ErrorFieldForm name={name} />
                                                    </InputGroupForm>
                                            ))}
                                    </div>
                                </div>
                            </SectionForm>

                            <SectionForm
                                icon={<Contact2 className="w-5 h-5" />}
                                title="PIC Teknis"
                                color="blue">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[
                                        { type: 'text', label: "Nama", name: "pic_name" },
                                        {
                                            type: 'select',
                                            label: 'Jabatan',
                                            name: 'pic_position',
                                            options: [
                                                { value: '', label: 'Pilih PIC Teknis' },
                                                { value: 'HelpDesk/ IT Support', label: 'HelpDesk/ IT Support' },
                                                { value: 'Humas', label: 'Humas' },
                                                { value: 'Keuangan/ Bendahara', label: 'Keuangan/ Bendahara' },
                                                ...(isZoneApp || isAcademicApp
                                                    ? [
                                                        { value: 'Pranata Komputer', label: 'Pranata Komputer' },
                                                        { value: 'Tata Usaha', label: 'Tata Usaha' },
                                                    ]
                                                    : []),
                                            ],
                                        },
                                        { type: 'text', label: "Kontak", name: "pic_contact" }
                                    ].filter(field => !field.hidden?.(values))
                                        .map(({ label, name, type, options }) => (
                                            type == "select" ? <InputGroupForm key={name} label={label} data-field={name}>
                                                <Field
                                                    as="select"
                                                    name={name}
                                                    className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]">

                                                    {options.map((opt) => (
                                                        <option disabled={opt.disabled ?? false} key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </Field>
                                                <ErrorFieldForm name={name} />
                                            </InputGroupForm> :
                                                <InputGroupForm key={name} label={label}>
                                                    <Field
                                                        type={type || "text"}
                                                        name={name}
                                                        className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-green-300"
                                                    />
                                                    <ErrorFieldForm name={name} />
                                                </InputGroupForm>
                                        ))}
                                </div>
                            </SectionForm>

                            <Signature
                                signedName1="Tanda Tangan Pemohon"
                                onChangeResult={(sig) => setFieldValue("signature", sig)}
                            />
                            <ErrorFieldForm name="signature" />

                            <div className="text-center sm:text-right pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium transition disabled:opacity-60 flex items-center gap-2 mx-auto sm:mx-0"
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center gap-2">
                                            <Loader className="animate-spin w-4 h-4" />
                                            <span>Mengirim...</span>
                                        </div>
                                    ) : (
                                        <>Simpan</>
                                    )}
                                </button>
                            </div>
                        </Form>
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default FormRequestApp;